<template>
  <div class="profile-page">
    <v-row>
      <v-col cols="12" md="4">
        <!-- Profile Card -->
        <v-card rounded="lg" class="text-center">
          <v-card-text class="pa-6">
            <v-avatar size="120" class="mb-4">
              <v-img
                v-if="user.profile_image"
                :src="getFileUrl(user.profile_image)"
                :alt="user.full_name"
              />
              <span v-else class="text-h3 font-weight-bold">
                {{ user.first_name?.[0] }}{{ user.last_name?.[0] }}
              </span>
            </v-avatar>
            
            <h2 class="text-h5 font-weight-bold mb-2">
              {{ user.full_name }}
            </h2>
            <p class="text-body-1 text-medium-emphasis mb-4">
              {{ user.email }}
            </p>
            
            <div v-if="boardMember" class="mb-4">
              <v-chip
                :color="positionColor"
                variant="tonal"
                size="large"
                prepend-icon="mdi-account-tie"
              >
                {{ boardMember.position }}
              </v-chip>
            </div>
            
            <v-btn
              color="primary"
              variant="outlined"
              @click="editDialog = true"
              block
            >
              <v-icon left>mdi-pencil</v-icon>
              Edit Profile
            </v-btn>
          </v-card-text>
        </v-card>
        
        <!-- Quick Stats -->
        <v-card rounded="lg" class="mt-4">
          <v-card-title>Activity Summary</v-card-title>
          <v-card-text>
            <!-- Loading skeletons -->
            <template v-if="statsLoading">
              <v-skeleton-loader type="list-item-avatar" class="mb-1" />
              <v-skeleton-loader type="list-item-avatar" class="mb-1" />
              <v-skeleton-loader type="list-item-avatar" />
            </template>

            <v-list v-else>
              <!-- Meetings -->
              <v-list-item>
                <template #prepend>
                  <v-icon color="primary">mdi-calendar-check</v-icon>
                </template>
                <v-list-item-title>Meetings Attended</v-list-item-title>
                <v-list-item-subtitle>
                  {{ stats.meetings_attended }} / {{ stats.total_meetings }}
                </v-list-item-subtitle>
                <template #append>
                  <v-chip
                    size="small"
                    :color="stats.total_meetings > 0 && (stats.meetings_attended / stats.total_meetings) >= 0.75 ? 'success' : 'warning'"
                    variant="tonal"
                  >
                    {{ stats.total_meetings > 0 ? Math.round((stats.meetings_attended / stats.total_meetings) * 100) : 0 }}%
                  </v-chip>
                </template>
              </v-list-item>

              <v-divider class="my-1" />

              <!-- Votes -->
              <v-list-item>
                <template #prepend>
                  <v-icon color="success">mdi-vote</v-icon>
                </template>
                <v-list-item-title>Votes Cast</v-list-item-title>
                <v-list-item-subtitle>On resolutions</v-list-item-subtitle>
                <template #append>
                  <v-chip size="small" color="success" variant="tonal">
                    {{ stats.votes_cast }}
                  </v-chip>
                </template>
              </v-list-item>

              <v-divider class="my-1" />

              <!-- Document comments -->
              <v-list-item>
                <template #prepend>
                  <v-icon color="info">mdi-comment-text</v-icon>
                </template>
                <v-list-item-title>Document Comments</v-list-item-title>
                <v-list-item-subtitle>On shared documents</v-list-item-subtitle>
                <template #append>
                  <v-chip size="small" color="info" variant="tonal">
                    {{ stats.comments }}
                  </v-chip>
                </template>
              </v-list-item>
            </v-list>
          </v-card-text>
        </v-card>
      </v-col>
      
      <v-col cols="12" md="8">
        <!-- Profile Information -->
        <v-card rounded="lg">
          <v-card-title>Profile Information</v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="12" sm="6">
                <v-text-field
                  label="First Name"
                  :model-value="user.first_name"
                  readonly
                  variant="outlined"
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  label="Last Name"
                  :model-value="user.last_name"
                  readonly
                  variant="outlined"
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  label="Email"
                  :model-value="user.email"
                  readonly
                  variant="outlined"
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  label="Phone"
                  :model-value="user.phone || 'Not provided'"
                  readonly
                  variant="outlined"
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  label="Username"
                  :model-value="user.username"
                  readonly
                  variant="outlined"
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  label="Member Since"
                  :model-value="formatDate(user.created_at)"
                  readonly
                  variant="outlined"
                />
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
        
        <!-- Board Member Information -->
        <v-card v-if="boardMember" rounded="lg" class="mt-4">
          <v-card-title>Board Member Details</v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="12" sm="6">
                <v-text-field
                  label="Position"
                  :model-value="boardMember.position"
                  readonly
                  variant="outlined"
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  label="Department"
                  :model-value="boardMember.department || 'Not specified'"
                  readonly
                  variant="outlined"
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  label="Start Date"
                  :model-value="formatDate(boardMember.start_date)"
                  readonly
                  variant="outlined"
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  label="Status"
                  :model-value="boardMember.is_current ? 'Active' : 'Inactive'"
                  readonly
                  variant="outlined"
                />
              </v-col>
              <v-col cols="12" v-if="boardMember.bio">
                <v-textarea
                  label="Bio"
                  :model-value="boardMember.bio"
                  readonly
                  variant="outlined"
                  rows="3"
                />
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
        
        <!-- Change Password -->
        <v-card rounded="lg" class="mt-4">
          <v-card-title>Security</v-card-title>
          <v-card-text>
            <v-btn
              color="warning"
              variant="outlined"
              @click="passwordDialog = true"
            >
              <v-icon left>mdi-key</v-icon>
              Change Password
            </v-btn>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    
    <!-- Edit Profile Dialog -->
    <v-dialog v-model="editDialog" max-width="600">
      <v-card>
        <v-card-title>Edit Profile</v-card-title>
        <v-card-text>
          <v-form ref="editFormRef" v-model="editFormValid">
            <v-row>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="editForm.first_name"
                  label="First Name"
                  :rules="nameRules"
                  variant="outlined"
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="editForm.last_name"
                  label="Last Name"
                  :rules="nameRules"
                  variant="outlined"
                />
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="editForm.email"
                  label="Email"
                  type="email"
                  :rules="emailRules"
                  variant="outlined"
                />
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="editForm.phone"
                  label="Phone (Optional)"
                  variant="outlined"
                />
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="editDialog = false">Cancel</v-btn>
          <v-btn
            color="primary"
            :loading="updating"
            @click="updateProfile"
          >
            Save Changes
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    
    <!-- Change Password Dialog -->
    <v-dialog v-model="passwordDialog" max-width="500">
      <v-card>
        <v-card-title>Change Password</v-card-title>
        <v-card-text>
          <v-form ref="passwordFormRef" v-model="passwordFormValid">
            <v-text-field
              v-model="passwordForm.current_password"
              label="Current Password"
              type="password"
              :rules="requiredRules"
              variant="outlined"
              class="mb-3"
            />
            <v-text-field
              v-model="passwordForm.new_password"
              label="New Password"
              type="password"
              :rules="passwordRules"
              variant="outlined"
              class="mb-3"
            />
            <v-text-field
              v-model="passwordForm.confirm_password"
              label="Confirm New Password"
              type="password"
              :rules="confirmPasswordRules"
              variant="outlined"
            />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="passwordDialog = false">Cancel</v-btn>
          <v-btn
            color="warning"
            :loading="changingPassword"
            @click="changePassword"
          >
            Change Password
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { ref, computed, reactive, onMounted } from 'vue'
import { useAuthStore } from '@/store/auth'
import { useToast } from 'vue-toastification'
import moment from 'moment'
import api, { getFileUrl } from '@/services/api'

export default {
  name: 'Profile',
  setup() {
    const authStore = useAuthStore()
    const toast = useToast()
    
    const editDialog = ref(false)
    const passwordDialog = ref(false)
    const editFormValid = ref(false)
    const passwordFormValid = ref(false)
    const updating = ref(false)
    const changingPassword = ref(false)
    const editFormRef = ref(null)
    const passwordFormRef = ref(null)
    
    const editForm = reactive({
      first_name: '',
      last_name: '',
      email: '',
      phone: ''
    })
    
    const passwordForm = reactive({
      current_password: '',
      new_password: '',
      confirm_password: ''
    })
    
    const stats = reactive({
      meetings_attended: 0,
      total_meetings: 0,
      votes_cast: 0,
      comments: 0
    })
    const statsLoading = ref(true)
    
    const user = computed(() => authStore.user || {})
    const boardMember = computed(() => user.value.board_member || null)
    
    const positionColor = computed(() => {
      const position = boardMember.value?.position?.toLowerCase()
      if (position?.includes('chairman') || position?.includes('chair')) return 'primary'
      if (position?.includes('secretary')) return 'info'
      if (position?.includes('treasurer')) return 'success'
      return 'grey'
    })
    
    const nameRules = [
      v => !!v || 'This field is required',
      v => (v && v.length >= 2) || 'Must be at least 2 characters'
    ]
    
    const emailRules = [
      v => !!v || 'Email is required',
      v => /.+@.+\..+/.test(v) || 'Email must be valid'
    ]
    
    const requiredRules = [
      v => !!v || 'This field is required'
    ]
    
    const passwordRules = [
      v => !!v || 'Password is required',
      v => (v && v.length >= 6) || 'Password must be at least 6 characters'
    ]
    
    const confirmPasswordRules = [
      v => !!v || 'Please confirm your password',
      v => v === passwordForm.new_password || 'Passwords do not match'
    ]
    
    const formatDate = (dateString) => {
      return moment(dateString).format('MMM DD, YYYY')
    }
    
    const initializeEditForm = () => {
      editForm.first_name = user.value.first_name || ''
      editForm.last_name = user.value.last_name || ''
      editForm.email = user.value.email || ''
      editForm.phone = user.value.phone || ''
    }
    
    const updateProfile = async () => {
      if (!editFormValid.value) return
      
      updating.value = true
      try {
        const result = await authStore.updateProfile(editForm)
        if (result.success) {
          toast.success('Profile updated successfully')
          editDialog.value = false
        } else {
          toast.error(result.message)
        }
      } catch (error) {
        toast.error('Failed to update profile')
      } finally {
        updating.value = false
      }
    }
    
    const changePassword = async () => {
      if (!passwordFormValid.value) return
      
      changingPassword.value = true
      try {
        const result = await authStore.changePassword({
          current_password: passwordForm.current_password,
          new_password: passwordForm.new_password
        })
        
        if (result.success) {
          toast.success('Password changed successfully')
          passwordDialog.value = false
          passwordForm.current_password = ''
          passwordForm.new_password = ''
          passwordForm.confirm_password = ''
        } else {
          toast.error(result.message)
        }
      } catch (error) {
        toast.error('Failed to change password')
      } finally {
        changingPassword.value = false
      }
    }
    
    const loadStats = async () => {
      try {
        const response = await api.get('/users/me/stats')
        stats.meetings_attended = response.data.meetings_attended
        stats.total_meetings = response.data.total_meetings
        stats.votes_cast = response.data.votes_cast
        stats.comments = response.data.comments
      } catch (e) {
        console.error('Failed to load stats', e)
      } finally {
        statsLoading.value = false
      }
    }

    onMounted(() => {
      initializeEditForm()
      loadStats()
    })
    
    return {
      editDialog,
      passwordDialog,
      editFormValid,
      passwordFormValid,
      updating,
      changingPassword,
      editForm,
      passwordForm,
      stats,
      statsLoading,
      user,
      boardMember,
      positionColor,
      nameRules,
      emailRules,
      requiredRules,
      passwordRules,
      confirmPasswordRules,
      formatDate,
      getFileUrl,
      updateProfile,
      changePassword
    }
  }
}
</script>

<style scoped lang="scss">
.profile-page {
  max-width: 1200px;
  margin: 0 auto;
}

.v-avatar {
  border: 4px solid rgba(var(--v-theme-primary), 0.1);
}

.v-card {
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1) !important;
  }
}
</style>